// =============================================================================
// SaaS-Admin data service — talks to the .NET SaaS Tracker back-office API
// (/saastracker/admin/*). Same shape as pricingConfigService.ts: one fetch
// helper, a Cognito bearer token, typed wrappers.
//
// Why not /me/summary: that endpoint resolves ONE tenant from the caller's JWT
// `sub` (or an X-Tenant-Id header) via CurrentTenantMiddleware, and returns no
// signup date. It answers "how am I doing?" for a tenant inside the main app.
// The back office needs every tenant at once, so it uses /admin/tenants.
// =============================================================================
import { getIdToken } from '../../../services/cognitoAuth';

const API_ROOT =
  (process.env.REACT_APP_SAAS_TRACKER_URL as string | undefined) ?? 'http://localhost:5080/saastracker';

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try { const t = await getIdToken(); if (t) h.Authorization = `Bearer ${t}`; } catch { /* dev: no auth */ }
  return h;
}

async function req<T>(path: string, method: string = 'GET', body?: any): Promise<T> {
  const res = await fetch(`${API_ROOT}${path}`, {
    method,
    headers: await authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    // The API returns { error: "..." } on 4xx — surface that instead of a bare code.
    let detail = '';
    try { const j = JSON.parse(await res.text()); detail = j?.error ? ` — ${j.error}` : ''; } catch { /* non-JSON body */ }
    throw new Error(`${method} ${path} failed (${res.status})${detail}`);
  }
  const text = await res.text();
  return (text ? JSON.parse(text) : null) as T;
}

/** Drops undefined/empty/"all" so the API sees only real filters. */
function qs(params: Record<string, string | number | undefined | null>): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    const s = String(v).trim();
    if (!s || s === 'all') return;
    sp.append(k, s);
  });
  const q = sp.toString();
  return q ? `?${q}` : '';
}

// ---- Types ------------------------------------------------------------------

/** Resolved by Lifecycle.Resolve server-side — matches what the tenant's own app shows. */
export type TenantStatus = 'Trial' | 'Active' | 'Deactivated' | 'Suspended';

/** Severity band: critical >= 90% used, low >= 75%. */
export type TenantLevel = 'ok' | 'low' | 'critical' | 'trial-ending' | 'deactivated';

export interface AdminTenantRow {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  plan: string | null;
  status: TenantStatus;
  level: TenantLevel;
  blocked: boolean;
  balance: number;
  currency: string;
  /** Sum of positive ledger entries — the prepaid equivalent of "allocated". */
  totalGranted: number;
  /** Sum of Deduct entries, absolute. */
  totalUsed: number;
  pctUsed: number;
  /** Lifetime completed purchases. Replaces the old mock `mrr`. */
  totalPaid: number;
  purchaseCount: number;
  createdAt: string;
  trialEndsAt: string | null;
  trialDaysLeft: number;
  lastActivityAt: string | null;
}

export interface AdminTenantListResponse {
  total: number;
  page: number;
  pageSize: number;
  items: AdminTenantRow[];
}

export interface LedgerEntry {
  id: string; tenantId: string; type: string; amount: number; balanceAfter: number;
  actionKey: string | null; operationKey: string | null; reference: string | null;
  userRef: string | null; createdAt: string;
}
export interface UsageEntry {
  id: number; tenantId: string; actionKey: string; creditCost: number;
  userRef: string | null; at: string;
}
export interface TransactionEntry {
  id: string; tenantId: string; provider: string; orderId: string | null; captureId: string | null;
  amount: number; credits: number; currency: string; status: string;
  createdAt: string; completedAt: string | null;
}
export interface InvoiceEntry {
  id: string; tenantId: string; transactionId: string; number: string;
  amount: number; currency: string; pdfRef: string | null;
  emailedAt: string | null; createdAt: string;
}
export interface NotificationEntry {
  id: string; tenantId: string; event: string; title: string; body: string;
  severity: string; createdAt: string; readAt: string | null;
}

export interface AdminTenantDetail {
  tenant: AdminTenantRow;
  ledger: LedgerEntry[];
  usage: UsageEntry[];
  transactions: TransactionEntry[];
  invoices: InvoiceEntry[];
  notifications: NotificationEntry[];
}

export interface TenantQuery {
  search?: string;
  status?: string;
  plan?: string;
  level?: string;
  /** usage (default) | name | balance | paid | newest | oldest */
  sort?: string;
  page?: number;
  pageSize?: number;
}

// ---- Overview KPIs ----------------------------------------------------------

/**
 * Prepaid model: there is no MRR and nothing is ever "overdue" — an invoice is a
 * receipt for credit already bought. `revenue*` is completed gateway purchases,
 * dated by capture; `walletBalanceTotal` is unspent credit still owed as service.
 */
export interface AdminKpiResponse {
  totalTenants: number;
  activeTenants: number;
  trialTenants: number;
  deactivatedTenants: number;
  suspendedTenants: number;
  signupsThisMonth: number;
  /** pctUsed >= 80 */
  tenantsNearingLimit: number;
  tenantsExhausted: number;
  trialsEndingSoon: number;
  walletBalanceTotal: number;
  totalCreditsGranted: number;
  totalCreditsUsed: number;
  averageUtilization: number;
  revenueThisMonth: number;
  revenueLast30Days: number;
  revenueAllTime: number;
  topUpsThisMonth: number;
  /** Created/Pending — the buyer never finished. */
  openTopUps: number;
  failedTopUps: number;
  currency: string;
}

export const getKpis = () => req<AdminKpiResponse>('/admin/kpis');

// ---- Tenants ----------------------------------------------------------------

export const getTenants = (q: TenantQuery = {}) =>
  req<AdminTenantListResponse>(`/admin/tenants${qs(q as Record<string, any>)}`);

export const getTenantDetail = (id: string) =>
  req<AdminTenantDetail>(`/admin/tenants/${id}`);

/**
 * Pin a tenant's lifecycle state. Note that only `Suspended` truly sticks:
 * Lifecycle.Resolve recomputes every other status from credit + trial state on
 * the next read, so setting `Active` means "release the hold", not "force active".
 */
export const setTenantStatus = (id: string, status: TenantStatus) =>
  req<{ tenantId: string; status: TenantStatus }>(`/admin/tenants/${id}/status`, 'POST', { status });

// ---- Usage tracking ---------------------------------------------------------

export interface AdminUsageActionRow {
  actionKey: string;
  module: string;
  displayName: string;
  count: number;
  credits: number;
}

export interface AdminUsageDayRow {
  /** ISO date, e.g. "2026-08-05" (serialised from a .NET DateOnly). */
  day: string;
  count: number;
  credits: number;
}

/**
 * Scope matters here: `lifetime*` fields and `tenants` are all-time, while
 * `*InWindow`, `byAction` and `byDay` respect the `days` parameter. A prepaid
 * wallet is not a monthly quota, so "used this week" is not what tells you which
 * tenant is about to run dry — that is the lifetime utilisation.
 */
export interface AdminUsageResponse {
  days: number;
  lifetimeCreditsGranted: number;
  lifetimeCreditsUsed: number;
  lifetimeAverageUtilization: number;
  creditsUsedInWindow: number;
  actionsInWindow: number;
  topUpsInWindow: number;
  tenants: AdminTenantRow[];
  byAction: AdminUsageActionRow[];
  byDay: AdminUsageDayRow[];
}

export const getUsage = (days: number = 30) =>
  req<AdminUsageResponse>(`/admin/usage${qs({ days })}`);

// ---- Invoices ---------------------------------------------------------------

/**
 * Derived from the invoice's transaction, not stored on the invoice: `paid` when
 * the capture completed, `pending` while it is still in flight. Prepaid means
 * there is no due date and no such thing as overdue.
 */
export type InvoiceStatus = 'paid' | 'pending' | 'failed' | 'refunded';

export interface AdminInvoiceRow {
  id: string;
  number: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string | null;
  amount: number;
  currency: string;
  status: InvoiceStatus;
  issuedAt: string;
  paidAt: string | null;
  emailedAt: string | null;
  transactionId: string;
  provider: string | null;
  orderId: string | null;
  captureId: string | null;
  /** Relative to the API root, e.g. "/admin/invoices/{id}/pdf". */
  pdfPath: string;
}

export interface AdminInvoiceListResponse {
  total: number;
  page: number;
  pageSize: number;
  /** Whole-book totals — not scoped to the page or the active filter. */
  totalCollected: number;
  totalPending: number;
  totalRefunded: number;
  currency: string;
  items: AdminInvoiceRow[];
}

export interface InvoiceQuery {
  search?: string;
  status?: string;
  /** ISO instants; the page sends start-of-day / end-of-day in UTC. */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export const getInvoices = (q: InvoiceQuery = {}) =>
  req<AdminInvoiceListResponse>(`/admin/invoices${qs(q as Record<string, any>)}`);

/**
 * The PDF route is bearer-gated, so it cannot be a plain <a href>. Fetch it as a
 * blob and open it; if the browser blocks the new tab (common for a window.open
 * after an await), fall back to downloading it so the click is never a no-op.
 */
export async function openInvoicePdf(pdfPath: string, fileName?: string): Promise<void> {
  const res = await fetch(`${API_ROOT}${pdfPath}`, { headers: await authHeaders() });
  if (!res.ok) throw new Error(`Could not load the PDF (${res.status})`);

  const url = URL.createObjectURL(await res.blob());
  const win = window.open(url, '_blank');
  if (!win) {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || 'invoice.pdf';
    a.click();
  }
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/** Walks the pages so an export covers the whole filtered set, not just page 1. */
export async function getAllInvoices(q: InvoiceQuery = {}, cap: number = 5000): Promise<AdminInvoiceRow[]> {
  const pageSize = 200;
  const out: AdminInvoiceRow[] = [];
  for (let page = 1; out.length < cap; page++) {
    const res = await getInvoices({ ...q, page, pageSize });
    out.push(...res.items);
    if (res.items.length < pageSize || out.length >= res.total) break;
  }
  return out;
}

// ---- Payments (transactions) ------------------------------------------------

/** Mirrors TransactionStatus: 0 Created, 1 Pending, 2 Completed, 3 Failed, 4 Refunded. */
export type TransactionStatus = 'Created' | 'Pending' | 'Completed' | 'Failed' | 'Refunded';

export interface AdminPaymentRow {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantEmail: string | null;
  tenantPhone: string | null;
  amount: number;
  credits: number;
  currency: string;
  provider: string;
  orderId: string | null;
  captureId: string | null;
  status: TransactionStatus;
  createdAt: string;
  completedAt: string | null;
  ageHours: number;
  /** A receipt was raised — only ever true for a settled purchase. */
  invoiced: boolean;
  /**
   * PayPal took the money and credits were NOT granted. This is the one state
   * that costs a customer real money, and without the flag it is invisible: the
   * row looks exactly like an abandoned checkout.
   */
  needsReview: boolean;
  /** When it was first seen as charged-but-not-credited. */
  reviewRequiredAt: string | null;
  /** Why credits were withheld, in words. */
  failureReason: string | null;
  /** The PayPal capture that took the money — use this to find or refund it. */
  failureCaptureId: string | null;
}

/**
 * The unfinished pile. Not a dunning queue: prepaid top-ups are one-shot PayPal
 * checkouts that are never retried, so most of what lands here is someone who
 * opened the PayPal window and closed it again.
 */
export interface AdminPaymentQueueResponse {
  total: number;
  /** Created/Pending only — refunds and failures were never "at risk". */
  amountAtRisk: number;
  /** Open for longer than the stale window (24h). */
  abandoned: number;
  /** Open but still fresh — may yet complete. */
  pending: number;
  failed: number;
  refunded: number;
  /** Charged but not credited. Counted across ALL transactions, not just this list. */
  needsReview: number;
  /** Money taken from customers who have nothing to show for it. */
  amountNeedingReview: number;
  items: AdminPaymentRow[];
}

export interface AdminTransactionListResponse {
  total: number;
  page: number;
  pageSize: number;
  items: AdminPaymentRow[];
}

export interface AdminReconcileRow {
  transactionId: string;
  tenantId: string;
  tenantName: string;
  orderId: string | null;
  captureId: string;
  amount: number;
  currency: string;
  completedAt: string;
  invoiceCreated: boolean;
}

export interface AdminReconcileResponse {
  scanned: number;
  repaired: number;
  invoicesCreated: number;
  items: AdminReconcileRow[];
}

export const getPaymentQueue = () =>
  req<AdminPaymentQueueResponse>('/admin/payments/queue');

export const getTransactions = (
  q: { status?: string; search?: string; page?: number; pageSize?: number } = {},
) => req<AdminTransactionListResponse>(`/admin/transactions${qs(q as Record<string, any>)}`);

/**
 * Repairs transactions PayPal really did capture but that were never stamped
 * Completed. Only touches rows that already carry a `paypal:<captureId>` purchase
 * on the ledger, so it can never invent a payment — and it never calls PayPal.
 */
export const reconcilePayments = () =>
  req<AdminReconcileResponse>('/admin/payments/reconcile', 'POST');

// ---- Reminders --------------------------------------------------------------

/**
 * Email and WhatsApp are outbound. InApp is not — it is the popup the main app
 * polls from /me/notifications and renders bottom-right. It has a template so
 * the popup can carry its own short copy (and be switched off) rather than
 * reusing the email body.
 */
export type NotificationChannel = 'Email' | 'WhatsApp' | 'InApp';

export interface AdminReminderTemplateRow {
  id: string;
  channel: NotificationChannel;
  /** e.g. "threshold.75", "credit.exhausted", "trial.ended", "welcome". */
  event: string;
  triggerLabel: string;
  subject: string | null;
  body: string;
  active: boolean;
  /** True when the reminder sweep raises this event on its own. */
  automated: boolean;
  /** Real sends, counted from the in-app notification stream. */
  totalSent: number;
  lastSentAt: string | null;
  updatedAt: string;
}

export interface AdminReminderTemplateUpsert {
  /** Omit to upsert by (channel, event); supply to edit a known row. */
  id?: string | null;
  channel: NotificationChannel;
  event: string;
  subject?: string | null;
  /** Null leaves the stored value alone — a partial save must not blank the body. */
  body?: string | null;
  /** Null leaves the stored value alone — so saving copy cannot silently pause it. */
  active?: boolean | null;
}

export interface AdminReminderHistoryRow {
  id: string;
  tenantId: string;
  tenantName: string;
  event: string;
  triggerLabel: string;
  title: string;
  body: string;
  severity: 'info' | 'warning' | 'critical' | string;
  status: 'delivered' | 'opened';
  sentAt: string;
  readAt: string | null;
}

export interface AdminReminderHistoryResponse {
  total: number;
  page: number;
  pageSize: number;
  items: AdminReminderHistoryRow[];
}

export interface AdminReminderAudienceRow {
  tenant: AdminTenantRow;
  /** The engine's once-only ledger already has this tenant+event. */
  alreadySent: boolean;
  /** When the popup for this event was last written for this tenant. */
  lastSentAt: string | null;
}

export interface AdminReminderAudienceResponse {
  event: string;
  triggerLabel: string;
  /** Plain-English statement of the rule that picked these tenants. */
  match: string;
  automated: boolean;
  total: number;
  items: AdminReminderAudienceRow[];
}

/**
 * Who this event applies to right now. Threshold events are BANDED — the 50%
 * list stops where the 75% list starts — so each tenant shows under exactly one
 * threshold card.
 */
export const getReminderAudience = (event: string) =>
  req<AdminReminderAudienceResponse>(
    `/admin/reminders/events/${encodeURIComponent(event)}/audience`);

/**
 * Operator override: sends one event to the chosen tenants even if they already
 * received it. The automatic sweep can only ever send an event once, so
 * re-sending has to be deliberate. Confirm before calling.
 */
export const sendReminder = (event: string, tenantIds: string[]) =>
  req<{ event: string; requested: number; sent: number }>(
    '/admin/reminders/send', 'POST', { event, tenantIds });

export interface ReminderSweepResult {
  tenants: number;
  sent: number;
  detail: { tenantId: string; tenantName: string; sent: string[] }[];
}

export const getReminderTemplates = () =>
  req<AdminReminderTemplateRow[]>('/admin/reminders/templates');

export const saveReminderTemplate = (t: AdminReminderTemplateUpsert) =>
  req<AdminReminderTemplateRow>('/admin/reminders/templates', 'PUT', t);

export const getReminderHistory = (
  q: { search?: string; event?: string; page?: number; pageSize?: number } = {},
) => req<AdminReminderHistoryResponse>(`/admin/reminders/history${qs(q as Record<string, any>)}`);

/**
 * Evaluates every tenant now and sends whatever is due. This is a real send over
 * email/WhatsApp and it moves tenant status — always confirm before calling.
 */
export const runReminderSweep = () =>
  req<ReminderSweepResult>('/admin/reminders/run', 'POST');

/** Same evaluation, one tenant. Note: not under /admin — it predates that group. */
export const runTenantReminders = (tenantId: string) =>
  req<{ tenantId: string; tenantName: string; sent: string[] }>(
    `/tenants/${tenantId}/reminders/run`, 'POST');

/** Walks the pages so an export covers the whole filtered set, not just page 1. */
export async function getAllTenants(q: TenantQuery = {}, cap: number = 5000): Promise<AdminTenantRow[]> {
  const pageSize = 200;
  const out: AdminTenantRow[] = [];
  for (let page = 1; out.length < cap; page++) {
    const res = await getTenants({ ...q, page, pageSize });
    out.push(...res.items);
    if (res.items.length < pageSize || out.length >= res.total) break;
  }
  return out;
}

// ---- Credit adjustments (refunds, goodwill credit, corrections) --------------

export interface CreditAdjustmentResult {
  tenantId: string;
  amount: number;
  balance: number;
  status: string | null;
  /** True when the same requestId was sent before; nothing was applied twice. */
  alreadyApplied: boolean;
  /** What the ledger shows: the reason and the admin who made it. */
  reference: string;
}

/**
 * Adds (positive amount) or removes (negative amount) credits on a tenant's wallet as an
 * Adjustment ledger entry. requestId makes a double click safe: send the same id for one
 * adjustment. Removing more than the balance is refused.
 */
export const adjustTenantCredits = (tenantId: string, amount: number, reason: string, requestId: string) =>
  req<CreditAdjustmentResult>(`/admin/tenants/${tenantId}/credit-adjustments`, 'POST', { amount, reason, requestId });

// ---- Background jobs ---------------------------------------------------------

export interface ReconcileRow {
  tenantGuid: string; tenantId: string | null;
  aggregateCount: number; userActionsCount: number;
  aggregatePoints: number; userActionsPoints: number; ledgerPoints: number;
  match: boolean;
}

export interface ArchiveRunResult {
  startedUtc: string; finishedUtc: string; skipped: boolean; skipReason: string | null;
  failures: number; errors: string[];
  [key: string]: any;
}

export interface JobStatusResponse {
  checkedAtUtc: string;
  /** Things that need a look, in plain words. Empty = all fine. */
  problems: string[];
  queue: {
    last24Hours: { charged: number; pending: number; notBillable: number; tenantNotFound: number };
    pendingOver15Minutes: number;
    lastActionAtUtc: string | null;
  };
  reconcile: { dateUtc: string; tenants: number; mismatches: number; rows: ReconcileRow[] };
  userActionArchive: {
    nightlyEnabled: boolean; runAtUtc: string; waitingRows: number; oldestWaitingUtc: string | null;
    files: number; lastFileWrittenUtc: string | null; lastRunOfThisTask: ArchiveRunResult | null;
    /** Monthly cleanup: SQL keeps the current month only (last month is deleted from the 2nd). */
    deleteEnabled?: boolean; keptFromUtc?: string; rowsOlderThanKept?: number;
    daysDeleted?: number; daysBeingDeleted?: number;
  };
  creditHistoryArchive: {
    nightlyEnabled: boolean; deleteEnabled: boolean; runAtUtc: string; keptFromMonth: string;
    files: number; deletedMonths: number; monthsBeingDeleted: number; lastFileWrittenUtc: string | null;
    rowsOlderThanKept: number; rowsWithoutTenantGuid: number; lastRunOfThisTask: ArchiveRunResult | null;
  };
  priceCache: { actions: number; loadedAtUtc: string | null; lastError: string | null };
}

/** reconcileDate: 'YYYY-MM-DD' (UTC day); default yesterday. */
export const getJobStatus = (reconcileDate?: string) =>
  req<JobStatusResponse>(`/admin/jobs/status${qs({ reconcileDateUtc: reconcileDate })}`);

// ---- Tenant credit history (same list the tenant sees in Billing and credits) ----

/** Filters for a tenant's credit history. Dates are ISO strings in UTC; toUtc is exclusive. At most 3 months. */
export interface CreditHistoryQuery {
  page?: number;
  pageSize?: number;
  fromUtc?: string;
  toUtc?: string;
  sort?: 'newest' | 'oldest' | 'credits_desc' | 'credits_asc';
  /** true = up to 10,000 rows in one page, for export. */
  export?: boolean;
}

export interface CreditHistoryItem {
  id: number;
  actionKey: string;
  creditCost: number;
  userRef: string | null;
  at: string;
  tenantGuid: string | null;
}

/** total and totalCredits cover every row of the period, wherever it is stored (database or S3 archive). */
export interface CreditHistoryResult {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  totalCredits: number;
  items: CreditHistoryItem[];
}

export const searchTenantCreditHistory = (tenantId: string, q: CreditHistoryQuery) =>
  req<CreditHistoryResult>(`/admin/tenants/${tenantId}/usage/search`, 'POST', q);

// ---- Tenant activity log (UserActions), one month at a time ----

export type ChargeStatus = 'Charged' | 'NotBillable' | 'Pending' | 'TenantNotFound';

export interface TenantUserActionItem {
  userActionId: number;
  /** UTC, ISO with Z. */
  createdDateUtc: string;
  moduleName: string | null;
  actionName: string;
  /** Readable name from the price list, or the action name. */
  actionLabel: string;
  pointsUsed: number;
  chargeStatus: ChargeStatus | null;
  userName: string | null;
}

/** total and totalPoints cover every row of the month for the chosen status; statusCounts covers all statuses. */
export interface TenantUserActionResult {
  month: string;
  total: number;
  totalPoints: number;
  page: number;
  pageSize: number;
  totalPages: number;
  statusCounts: Record<string, number>;
  /** Days of the month read from the S3 archive (already deleted from the database). */
  daysFromArchive: number;
  items: TenantUserActionItem[];
}

/** month: 'YYYY-MM' (UTC month). chargeStatus empty = all statuses. */
export const getTenantUserActions = (
  tenantId: string, month: string, chargeStatus: string = '', page: number = 1, pageSize: number = 50,
) =>
  req<TenantUserActionResult>(
    `/admin/tenants/${tenantId}/user-actions${qs({ month, chargeStatus: chargeStatus || undefined, page, pageSize })}`);

// ---- Tenant prices: a tenant's own credit price for selected actions ----

export interface TenantPriceItem {
  actionName: string;
  moduleName: string | null;
  functionName: string | null;
  defaultPoints: number;
  /** The tenant's own price, or null when it pays the default. */
  tenantPoints: number | null;
  isCustom: boolean;
  reason: string | null;
  changedBy: string | null;
  changedDateUtc: string | null;
}

export interface TenantPriceHistoryRow {
  actionName: string;
  points: number;
  isActive: boolean;
  reason: string | null;
  createdDateUtc: string;
  createdBy: string | null;
  modifiedDateUtc: string | null;
  modifiedBy: string | null;
}

export interface TenantPricesResponse {
  tenantGuid: string;
  customCount: number;
  items: TenantPriceItem[];
  /** Last 50 own-price rows, newest first (active and replaced). */
  history: TenantPriceHistoryRow[];
}

export const getTenantPrices = (tenantId: string) =>
  req<TenantPricesResponse>(`/admin/tenants/${tenantId}/prices`);

/** Sets the tenant's own price for one action (0 = free). Reason: 5 to 300 characters. */
export const setTenantPrice = (tenantId: string, actionName: string, points: number, reason: string) =>
  req<any>(`/admin/tenants/${tenantId}/prices/${encodeURIComponent(actionName)}`, 'PUT', { points, reason });

/** Back to the default price for one action. */
export const resetTenantPrice = (tenantId: string, actionName: string) =>
  req<any>(`/admin/tenants/${tenantId}/prices/${encodeURIComponent(actionName)}`, 'DELETE');
